import axios from "axios";
import { getApplicationApiUrl } from "../utils/config-utils";
import { errorCategoryMapper } from "../utils/errors-utils";
import { isValidErrorFormat } from "../utils/errors-utils";
import { getStore } from "../store/storeRegistry";
import { setGlobalError } from "../store/reducers/errorSlice";
import {
  pinRequired,
  securityLogoutRequired,
  sessionLocked,
} from "../store/reducers/sessionSlice";
import {
  InvalidSessionErrorsCodes,
  PinErrorCodes,
  SilencedErrorCodes,
} from "../types/error.definition";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * MED-1622: never open the global error modal for this request (e.g. the
     * background activity ping). PIN lock codes are still acted on.
     */
    silent?: boolean;
  }
}

const backendApiTimeout = 15000;

// Primary API client
const API_CONFIG = {
  timeout: backendApiTimeout,
  ...(getApplicationApiUrl() && { baseURL: getApplicationApiUrl() }),
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

// Create axios instance
const apiClient = axios.create(API_CONFIG);

// Add interceptors to primary instance
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const errorResponse = error?.response?.data;
    const silent = error?.config?.silent === true;
    // A PIN code is acted on even when the body carries no message.
    if (
      errorResponse &&
      (isValidErrorFormat(errorResponse) ||
        PinErrorCodes.includes(errorResponse.code))
    ) {
      const mapped = errorCategoryMapper(errorResponse);
      // MED-1622: any call can find the session locked or the PIN missing —
      // the lock provider then shows the unlock / create screen.
      if (mapped.code === "SESSION_LOCKED") {
        getStore()?.dispatch(sessionLocked());
      } else if (mapped.code === "PIN_REQUIRED") {
        getStore()?.dispatch(pinRequired());
      } else if (
        mapped.code === "PIN_CREATE_REAUTH_REQUIRED" ||
        mapped.code === "SESSION_REUSE_DETECTED"
      ) {
        // The server closed the session: explain it, then go to login.
        getStore()?.dispatch(securityLogoutRequired(mapped.code));
      }
      // Dispatch to global error modal — skip session errors and silenced codes
      if (
        !silent &&
        !InvalidSessionErrorsCodes.includes(mapped.code) &&
        !SilencedErrorCodes.includes(mapped.code)
      ) {
        getStore()?.dispatch(setGlobalError(mapped.code));
      }
      return Promise.reject({ ...mapped });
    } else {
      if (!silent) getStore()?.dispatch(setGlobalError("UNHANDLED_ERROR"));
      return Promise.reject({
        code: "UNHANDLED_ERROR",
        category: "UNKNOWN_ERROR",
      });
    }
  }
);

// Add interceptors to primary instance
apiClient.interceptors.request.use(
  config => config,
  error => {
    const errorResponse = error?.response?.data;
    return Promise.reject({
      code: errorResponse.code,
      category: "BUSINESS_ERROR",
    });
  }
);

export default apiClient;
